import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  ResponsiveContainer,
} from "recharts";
import OverViewChart from "../../../components/dashboardcomponents/OverViewChart";

import { ReactComponent as UnLike } from "../../../assets/images/unLike.svg";
import { ReactComponent as Like } from "../../../assets/images/like.svg";
import MainHeaderAnalytics from "../../../components/dashboardcomponents/Analytics/MainHeaderAnalytics";
import TagUsage from "../../../components/dashboardcomponents/Analytics/Chats/TagUsage";
import ArticlePerformanceTable from "../../../components/dashboardcomponents/Analytics/KnowledgeBase/ArticlePerformanceTable";
import BubbleChart from "../../../components/dashboardcomponents/Analytics/KnowledgeBase/BubbleChart";
import { useDispatch, useSelector } from "react-redux";
import {
  getAnalyticsKbStats,
  initAnalyticsKbStats,
  setDateRange,
} from "../../../store/features/getAnalyticsKbStats";
import {
  getKbTagUsageStats,
  initKbTagUsageStats,
} from "../../../store/features/getKbTagUsageStats";
import {
  getKbArticlePerformance,
  initKbArticlePerformance,
} from "../../../store/features/getAnalyticsKbArticlePerformance";
import {
  getKbTopSearchTermsStats,
  initKbTopSearchTermsStats,
} from "../../../store/features/getKbTopSearchTerms";
import { useAnalyticsExport } from "../../../hooks/useAnalyticsExport";
import { useAnalyticsGroupId } from "../../../hooks/useAnalyticsGroupId";

function MainKnowledgeBase() {
  const {
    data: analyticsKbStatsData,
    currentRange,
    dateRange,
    loading: analyticsKbStatsLoading,
  } = useSelector((state) => state.analyticsKbStats);
  const kbTagUsageStats = useSelector((state) => state.kbTagUsageStats);
  const topSearchTermsStats = useSelector((state) => state.topSearchTermsStats);

  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5);

  const kbArticlePerformance = useSelector(
    (state) => state.kbArticlePerformance,
  );

  const kbChartsLoading = useMemo(
    () =>
      !!(
        analyticsKbStatsLoading ||
        kbTagUsageStats?.loading ||
        topSearchTermsStats?.loading
      ),
    [
      analyticsKbStatsLoading,
      kbTagUsageStats?.loading,
      topSearchTermsStats?.loading,
    ]
  );

  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();

  const {
    exportSectionOptions,
    exportSection,
    setExportSection,
    handleExport,
    exportLoading,
  } = useAnalyticsExport("kb", currentRange, dateRange, selectedGroupId);

  const showTrendForRange = currentRange === "7d";
  const handleDateChange = async (newDateRange) => {
    if (!newDateRange?.startDate || !newDateRange?.endDate) return;

    await dispatch(
      setDateRange({
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate,
      }),
    );

    await Promise.all([
      dispatch(
        getKbArticlePerformance({
          startDate: newDateRange.startDate,
          endDate: newDateRange.endDate,
          page: 1,
          limit: rowsPerPage,
          group_id: selectedGroupId,
        }),
      ),
      dispatch(
        getAnalyticsKbStats({
          startDate: newDateRange.startDate,
          endDate: newDateRange.endDate,
          group_id: selectedGroupId,
        }),
      ),
      dispatch(
        getKbTagUsageStats({
          startDate: newDateRange.startDate,
          endDate: newDateRange.endDate,
          group_id: selectedGroupId,
        }),
      ),
      dispatch(
        getKbTopSearchTermsStats({
          startDate: newDateRange.startDate,
          endDate: newDateRange.endDate,
          group_id: selectedGroupId,
        }),
      ),
    ]);
  };
  const tagPerformanceData =
    kbTagUsageStats?.tagData?.map((item) => ({
      name: item.name,
      value: item.usageCount,

      tag_id: item.tagId,
    })) || [];

  const analyticsKbStats = [
    {
      title: "Total Articles",
      number: analyticsKbStatsData?.metrics?.totalArticles?.value || 0,
      textPercentage: showTrendForRange
        ? analyticsKbStatsData?.metrics?.totalArticles?.change || 0
        : null,
      value: showTrendForRange ? "vs last week" : undefined,
      showTrend: true,
      isText: false,
      isTimeValue: false,
      isPercentage: false,
    },
    {
      title: "Published",
      // `newlyPublished` only counts articles published inside the range; this
      // card sits beside Total Articles and Draft, which are absolute counts.
      number: analyticsKbStatsData?.metrics?.publishedArticles?.value || 0,
      textPercentage: showTrendForRange
        ? analyticsKbStatsData?.metrics?.publishedArticles?.change || 0
        : null,
      value: showTrendForRange ? "vs last week" : undefined,
      showTrend: true,
      isText: false,
      isTimeValue: false,
      isPercentage: false,
    },
    {
      title: "Draft",
      number: analyticsKbStatsData?.metrics?.draftArticles?.value || 0,
      textPercentage: showTrendForRange
        ? analyticsKbStatsData?.metrics?.draftArticles?.change || 0
        : null,
      value: showTrendForRange ? "vs last week" : undefined,
      showTrend: true,
      isText: false,
      isTimeValue: false,
      isPercentage: false,
    },
    {
      title: "Total Visitor",
      // `totalViews` counts page views, not people; the API reports visitors
      // separately and this card is labelled for visitors.
      number: analyticsKbStatsData?.metrics?.totalVisitors?.value || 0,
      textPercentage: showTrendForRange
        ? analyticsKbStatsData?.metrics?.totalVisitors?.change || 0
        : null,
      value: showTrendForRange ? "vs last week" : undefined,
      showTrend: true,
      isText: false,
      isTimeValue: false,
      isPercentage: false,
    },
    {
      title: "Most Viewed Article",
      number: analyticsKbStatsData?.metrics?.mostViewedArticle?.title || "-",
      value: undefined,
      textPercentage: null,
      showTrend: false,
      isText: true,
      isTimeValue: false,
      isPercentage: false,
    },
  ];
  useEffect(() => {
    if (!selectedGroupId) return;

    Promise.all([
      dispatch(initKbArticlePerformance({ group_id: selectedGroupId })),
      dispatch(initAnalyticsKbStats({ group_id: selectedGroupId })),
      dispatch(initKbTagUsageStats({ group_id: selectedGroupId })),
      dispatch(initKbTopSearchTermsStats({ group_id: selectedGroupId })),
    ]);
  }, [dispatch, selectedGroupId]);

  // Adapted data for TagUsage component (expects {name, count})

  const topSearchTermsAdaptedData = topSearchTermsStats?.data?.keywords
    ?.filter((d) => d.query !== null && d.query !== undefined && d.query !== '')
    ?.map((d) => ({
      name: d.query,
      count: d.count,
    })) || [];
  const handlePageChange = (newPage) => {
    setPage(newPage);
    dispatch(
      getKbArticlePerformance({
        page: newPage + 1, // API pages start from 1
        limit: rowsPerPage,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        group_id: selectedGroupId,
      }),
    );
  };

  return (
    <div className="Dashboard_Main_Containerxx">
      <MainHeaderAnalytics
        title="Knowledge Base"
        dateRange={dateRange}
        onDateChange={handleDateChange}
        exportSectionOptions={exportSectionOptions}
        exportSection={exportSection}
        onExportSectionChange={setExportSection}
        onExport={handleExport}
        exportLoading={exportLoading}
        selectedGroupId={selectedGroupId}
        onGroupChange={setSelectedGroupId}
      />
      <OverViewChart data={analyticsKbStats} loading={kbChartsLoading} />

      {/* Top Search Terms and Tag Usage Section */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginTop: "16px",
          maxHeight: { xs: "none", md: "488px" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: "16px",
          overflow: { xs: "auto", md: "hidden" },
        }}
      >
        <TagUsage
          title="Top Search Terms"
          description="Views per term"
          bgcolor="#D9F7BE"
          data={topSearchTermsAdaptedData}
          loading={kbChartsLoading}
          value={"Searches"}
        />
        <BubbleChart
          bgcolor="#FFEFD6"
          data={tagPerformanceData}
          loading={kbChartsLoading}
        />
      </Box>

      {/* Row for Tag Performance and Article Performance */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
        }}
      >
        {/* Article Performance Table - Now Full Width */}
        <ArticlePerformanceTable
          articles={kbArticlePerformance.articles || []}
          rowsPerPage={rowsPerPage}
          page={page}
          totalArticles={kbArticlePerformance?.pagination?.totalArticles ?? null}
          onPageChange={handlePageChange}
          loading={kbArticlePerformance.loading}
        />
      </Box>
    </div>
  );
}

export default MainKnowledgeBase;
